import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, lighten, Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentCopyOutlined } from '@mui/icons-material';
import { useToggle } from '@dfl/hook-utils';
import { FlexBox, Form, FormTextField, HandlerError, IconButton } from '@dfl/mui-react-common';
import useProductDuplicateForm from '../../hooks/useProductDuplicateForm';
import { LoadingButton } from '@mui/lab';
import themeConfig from 'settings/branding';
import { IProduct } from '../../interfaces/IProduct';
import { PRODUCT_CLONE_ERRORS } from '../../constants/product-clone.errors';

type Props = { iconButton?: boolean; record: IProduct };

const ProductCloneButton = ({ iconButton, record }: Props) => {
  const { t } = useTranslation('product');
  const { onOpen, onClose, isOpen } = useToggle();
  const { control, error, isLoading, onSubmit } = useProductDuplicateForm({ code: '' }, record?._id, onClose);

  return (
    <>
      {iconButton ? (
        <IconButton tooltip={t('clone.action')} onClick={onOpen}>
          <ContentCopyOutlined fontSize='small' color='primary' />
        </IconButton>
      ) : (
        <Button variant='contained' startIcon={<ContentCopyOutlined />} onClick={onOpen}>
          {t('clone.action')}
        </Button>
      )}

      <Dialog
        open={isOpen}
        onClose={onClose}
        aria-labelledby='duplicated-dialog-title'
        aria-describedby='duplicated-dialog-description'
        maxWidth={'xs'}
        fullWidth
      >
        <DialogTitle sx={{ padding: '16px 16px 8px 16px' }} lineHeight={1.5} id='duplicated-dialog-title'>
          <FlexBox gap={1} alignItems={'center'}>
            <Avatar sx={{ backgroundColor: lighten(themeConfig.theme.primary.main, 0.8), width: 25, height: 25, p: 1 }}>
              <ContentCopyOutlined color='primary' fontSize='small' sx={{ width: 15, height: 15 }} />
            </Avatar>
            <Typography variant='h2'>
              {t('clone.title')} <span className='!font-normal text-[15px]'>{record?.code}</span>
            </Typography>
          </FlexBox>
          <Typography color={'grey'} sx={{ mt: 0.5 }}>
            {t('clone.description')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <HandlerError error={error} errors={PRODUCT_CLONE_ERRORS} />
          <Form className={'mt-3'} control={control} onSubmit={onSubmit} id='duplicated-product'>
            <FormTextField required name='code' label={t('fields.code')} />
          </Form>
        </DialogContent>
        <DialogActions>
          <Button variant='grey' onClick={onClose}>
            {t('common:cancel')}
          </Button>
          <LoadingButton type='submit' form='duplicated-product' autoFocus variant={'contained'} loading={isLoading}>
            {t('common:confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default memo(ProductCloneButton);
